import { useCallback, useEffect, useState } from 'react';
import { Server, AlertCircle, MoreVertical, Briefcase, GitPullRequest, FileText, Trash2 } from 'lucide-react';
import { Skeleton } from '../components/ui/Skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { ContainerManagement } from '../components/ContainerManagement';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { LastSynced } from '../components/ui/LastSynced';
import { ToastContainer } from '../components/ui/Toast';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { InlineConfirmation } from '../components/ui/InlineConfirmation';

interface PRInstance {
  id: string;
  name: string;
  status: string;
  instanceType: string;
  region: string;
  publicIp?: string;
  privateIp?: string;
  created: Date | string;
}

interface TenantGroup {
  tenant: string;
  tenantName: string;
  tenantColor?: string | null;
  instances: PRInstance[];
}

export function PRInstances() {
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('pr_instances', 'read');
  const tenantMetaMap = useTenantMetaMap(canView);
  const { isAllowed: canManageInstance } = usePermission('pr_instances', 'manage_instance');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([]);
  const [selectedServer, setSelectedServer] = useState<{ id: string; name: string } | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'error' | 'warning' | 'info' }>>([]);
  const [destroyTarget, setDestroyTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDestroying, setIsDestroying] = useState(false);

  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;

  useActivityHeartbeat('cache:pr_instances', canView);

  const fetchPRInstances = useCallback(async () => {
    if (!canView) return;
    if (tenantsToQuery.length === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch all tenants to get their display names and colors
      const tenantsResponse = await apiClient.getTenants();
      const tenantsData = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const tenantMap: Map<string, { name: string; color?: string | null }> = new Map(
        tenantsData.map((t: any) => [t.code.toLowerCase(), { name: t.name, color: t.color }]),
      );

      const response = await apiClient.getPRInstances();
      setSyncedAt(response.syncedAt ?? null);
      const instances = response.instances || [];

      // Group instances by tenant (extracted from name after the dot)
      const grouped = new Map<string, PRInstance[]>();

      instances.forEach((instance: PRInstance) => {
        // Extract tenant code from name (e.g., "pr599.breedon" -> "breedon")
        const parts = instance.name.split('.');
        const tenantCode = parts.length > 1 ? parts[1].toLowerCase() : 'unknown';

        // Filter by selected tenants
        if (tenantsToQuery.length > 0 && !tenantsToQuery.some(t => t.toLowerCase() === tenantCode)) {
          return;
        }

        if (!grouped.has(tenantCode)) {
          grouped.set(tenantCode, []);
        }
        grouped.get(tenantCode)!.push(instance);
      });

      // Convert to array with tenant names and colors
      const groups: TenantGroup[] = Array.from(grouped.entries()).map(([tenant, instances]) => {
        const tenantInfo = tenantMap.get(tenant);
        return {
          tenant,
          tenantName: tenantInfo?.name || tenant,
          tenantColor: tenantInfo?.color ?? null,
          instances,
        };
      });

      // Sort by tenant name
      groups.sort((a, b) => a.tenantName.localeCompare(b.tenantName));

      setTenantGroups(groups);
    } catch (err: any) {
      console.error('Failed to fetch PR instances:', err);
      setError(err.message || 'Failed to load PR instances');
    } finally {
      setIsLoading(false);
    }
  }, [canView, tenantsToQuery.join(',')]);

  useEffect(() => {
    void fetchPRInstances();
  }, [fetchPRInstances]);

  // Refresh only when backend cache version changes.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['pr_instances'],
    onEvent: () => {
      void fetchPRInstances();
    },
  });

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('running') || statusLower === 'active') {
      return <Badge className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800">Running</Badge>;
    } else if (statusLower.includes('stopped') || statusLower === 'inactive') {
      return <Badge className="bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700">Stopped</Badge>;
    } else if (statusLower.includes('pending') || statusLower.includes('provisioning')) {
      return <Badge className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800">Pending</Badge>;
    } else {
      return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <Card key={j}>
                  <CardHeader>
                    <Skeleton className="h-5 w-40 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
        </div>
        </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalInstances = tenantGroups.reduce((sum, t) => sum + t.instances.length, 0);

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const confirmDestroy = async () => {
    if (!destroyTarget) return;
    setIsDestroying(true);
    try {
      const result = await apiClient.destroyInstance(destroyTarget.name);
      if (!result.canDestroy) {
        addToast(result.reason || 'Cannot destroy: no configuration found.', 'error');
      } else {
        addToast(`Destroy workflow started: ${result.runId}`, 'info');
        setDestroyTarget(null);
      }
    } catch (err: any) {
      addToast(`Failed to destroy: ${err.message}`, 'error');
    } finally {
      setIsDestroying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <PageHeader
          icon={GitPullRequest}
          iconColor="text-pink-600 dark:text-pink-400"
          iconBgColor="bg-pink-100 dark:bg-pink-900/30"
          description="Monitor status and logs for tenant PR dockerized instances (Hetzner)"
          count={totalInstances}
          countLabel="instances"
          countColor="text-pink-600 dark:text-pink-400"
        />
        <LastSynced iso={syncedAt} className="mt-2" />
      </div>
      {tenantGroups.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No PR instances found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {tenantGroups.map((group) => (
            <div key={group.tenant} className="space-y-4">
              <div className="flex items-center justify-between">
                <TenantHeading
                  tenant={resolveTenantMeta(tenantMetaMap, group.tenant, {
                    name: group.tenantName,
                    color: group.tenantColor,
                  })}
                  title={group.tenantName}
                />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {group.instances.length} instance{group.instances.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {group.instances.map((instance) => (
                  <Card key={instance.id} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-hidden">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                            <Server className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg truncate">{instance.name}</CardTitle>
                            <CardDescription className="font-mono text-xs mt-1 truncate">
                              ID: {instance.id}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getStatusBadge(instance.status)}
                          {canManageInstance && (
                            <DropdownMenu
                              trigger={
                                <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors">
                                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                              }
                              items={[
                                {
                                  label: 'Manage',
                                  icon: <FileText className="w-4 h-4" />,
                                  onClick: () => setSelectedServer({ id: instance.id, name: instance.name }),
                                },
                                {
                                  label: 'Manage Login',
                                  icon: <Briefcase className="w-4 h-4" />,
                                  onClick: () => window.open(`https://${instance.name}.maxiron.cloud/maximo`, '_blank'),
                                },
                                {
                                  label: 'Destroy Server',
                                  icon: <Trash2 className="w-4 h-4" />,
                                  onClick: () => setDestroyTarget({ id: instance.id, name: instance.name }),
                                  className: 'text-red-700 dark:text-red-400',
                                },
                              ]}
                              align="end"
                            />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Type:</span>
                          <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.instanceType}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Datacenter:</span>
                          <span className="text-gray-900 dark:text-gray-100 truncate text-right">{instance.region}</span>
                        </div>
                        {instance.privateIp && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Private IP:</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.privateIp}</span>
                          </div>
                        )}
                        {instance.publicIp && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Public IP:</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.publicIp}</span>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                          Created {formatDate(instance.created)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedServer && (
        <ContainerManagement
          serverId={selectedServer.id}
          serverName={selectedServer.name}
          isPR={true}
          onClose={() => setSelectedServer(null)}
          onDeleted={() => {
            setSelectedServer(null);
            // Refresh instances
            const fetchPRInstances = async () => {
              setIsLoading(true);
              setError(null);
              
              try {
                const tenantsResponse = await apiClient.getTenants();
                const tenantsData = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
                const tenantMap: Map<string, string> = new Map(
                  tenantsData.map((t: any) => [t.code.toLowerCase(), String(t.name || t.code)])
                );
                
                const response = await apiClient.getPRInstances();
                const instances = response.instances || [];
                
                const grouped = new Map<string, PRInstance[]>();
                
                instances.forEach((instance: PRInstance) => {
                  const parts = instance.name.split('.');
                  const tenantCode = parts.length > 1 ? parts[1].toLowerCase() : 'unknown';
                  
                  if (!grouped.has(tenantCode)) {
                    grouped.set(tenantCode, []);
                  }
                  grouped.get(tenantCode)!.push(instance);
                });
                
                const groups: TenantGroup[] = Array.from(grouped.entries()).map(([tenant, instances]) => ({
                  tenant,
                  tenantName: tenantMap.get(tenant) || tenant,
                  instances,
                }));
                
                groups.sort((a, b) => a.tenantName.localeCompare(b.tenantName));
                
                setTenantGroups(groups);
              } catch (err: any) {
                console.error('Failed to fetch PR instances:', err);
                setError(err.message || 'Failed to load PR instances');
              } finally {
                setIsLoading(false);
              }
            };
            fetchPRInstances();
          }}
        />
      )}

      <Dialog open={!!destroyTarget} onOpenChange={(open) => !open && setDestroyTarget(null)}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Destroy Server</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {destroyTarget && (
              <InlineConfirmation
                message={`This will trigger devops-manage-destroy for "${destroyTarget.name}". The server and all its data will be permanently deleted. Continue?`}
                onConfirm={confirmDestroy}
                onCancel={() => setDestroyTarget(null)}
                confirmText="Destroy"
                cancelText="Cancel"
                isLoading={isDestroying}
                loadingText="Submitting..."
                variant="danger"
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
